import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import {EventInput} from '@fullcalendar/core';
import plLocale from '@fullcalendar/core/locales/pl';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import {Alert, Box, Button, Checkbox, Collapse, Divider, Drawer, FormControlLabel, IconButton, MenuItem, Paper, TextField, Typography} from '@mui/material';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CreditAccountController} from '../../../../api/credit/credit-account.controller';
import {CreditGrantController} from '../../../../api/credit/credit-grant.controller';
import {StatusCallAccountController} from '../../../../api/status-calls/status-call-account.controller';
import {StatusCallController} from '../../../../api/status-calls/status-call.controller';
import {CreditAccountSummaryModel} from '../../../../model/credit/credit-account-summary.model';
import {CreditGrantTypeEnum} from '../../../../model/credit/credit-grant-type.enum';
import {StatusCallAccountSummaryModel} from '../../../../model/status-calls/status-call-account-summary.model';
import {StatusCallModel} from '../../../../model/status-calls/status-call.model';
import {statusCallColors} from '../../../status-calls/utils/status-call.utils';

type MeetingRow = {
  date: string
  time: string
  durationMinutes: number
  title: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  organizationId: string | null
  organizationName: string | null
}

const buildEmptyMeeting = (): MeetingRow => ({date: '', time: '10:00', durationMinutes: 60, title: ''})

export const AssignPackageDrawer = ({isOpen, onClose, organizationId, organizationName}: Props) => {
  const {t} = useTranslation()
  const tr = (key: string) => t(`organizations-page.assignPackage.${key}`)

  const [grantHours, setGrantHours] = useState<string>('0')
  const [grantType, setGrantType] = useState<CreditGrantTypeEnum>(CreditGrantTypeEnum.PURCHASE)
  const [grantValidUntil, setGrantValidUntil] = useState<string>('')
  const [grantValidForever, setGrantValidForever] = useState<boolean>(true)
  const [grantComment, setGrantComment] = useState<string>('')
  const [meetings, setMeetings] = useState<MeetingRow[]>([])
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [partialSuccess, setPartialSuccess] = useState<string | null>(null)
  const [calendarVisible, setCalendarVisible] = useState<boolean>(false)
  const [existingCalls, setExistingCalls] = useState<StatusCallModel[]>([])
  const [creditSummary, setCreditSummary] = useState<CreditAccountSummaryModel | null>(null)
  const [statusCallSummary, setStatusCallSummary] = useState<StatusCallAccountSummaryModel | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const prevMeetingsCountRef = useRef<number>(0)

  useEffect(() => {
    if (meetings.length > prevMeetingsCountRef.current) {
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current
        if (el) el.scrollTo({top: el.scrollHeight, behavior: 'smooth'})
      })
    }
    prevMeetingsCountRef.current = meetings.length
  }, [meetings.length])

  const loadSummaries = async (orgId: string) => {
    try {
      const [credit, statusCall] = await Promise.all([
        CreditAccountController.getCreditAccountSummary(orgId).catch(() => null),
        StatusCallAccountController.getStatusCallAccount(orgId).catch(() => null),
      ])
      setCreditSummary(credit)
      setStatusCallSummary(statusCall)
    } catch {
      setCreditSummary(null)
      setStatusCallSummary(null)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setGrantHours('0')
      setGrantType(CreditGrantTypeEnum.PURCHASE)
      setGrantValidUntil('')
      setGrantValidForever(true)
      setGrantComment('')
      setMeetings([])
      setSubmitting(false)
      setError(null)
      setPartialSuccess(null)
      setCalendarVisible(false)
      setExistingCalls([])
      setCreditSummary(null)
      setStatusCallSummary(null)
      if (organizationId) loadSummaries(organizationId)
    }
  }, [isOpen, organizationId])

  const fetchCalendar = async (from: string, to: string) => {
    try {
      const calls = await StatusCallController.getStatusCallsCalendar(from, to)
      setExistingCalls(calls)
    } catch {
      setExistingCalls([])
    }
  }

  const calendarEvents: EventInput[] = useMemo(() => {
    const existing: EventInput[] = existingCalls
      .filter(c => c.status !== 'CANCELLED')
      .map(c => {
        const start = new Date(c.scheduledAt)
        const end = new Date(start.getTime() + c.durationMinutes * 60_000)
        const color = statusCallColors[c.status]
        return {
          id: c.id,
          title: c.title ?? c.organizationName ?? 'Status call',
          start: start.toISOString(),
          end: end.toISOString(),
          backgroundColor: color,
          borderColor: color,
          textColor: 'white',
        }
      })
    const pending: EventInput[] = []
    meetings.forEach((m, idx) => {
      if (!m.date || !m.time) return
      const start = new Date(`${m.date}T${m.time}:00`)
      if (isNaN(start.getTime())) return
      const end = new Date(start.getTime() + m.durationMinutes * 60_000)
      pending.push({
        id: `pending-${idx}`,
        title: `(nowe) ${m.title || ''}`.trim(),
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: '#ff9800',
        borderColor: '#f57c00',
        textColor: 'white',
      })
    })
    return [...existing, ...pending]
  }, [existingCalls, meetings])

  const todayIso = new Date().toISOString().slice(0, 10)
  const grantHoursNum = parseFloat(grantHours)
  const grantValid = !isNaN(grantHoursNum) && grantHoursNum >= 0 && Math.abs((grantHoursNum * 2) - Math.round(grantHoursNum * 2)) < 1e-9
  const meetingsValid = meetings.every(m => {
    if (!m.date || !m.time) return false
    if (m.time.length < 4) return false
    const [hh, mm] = m.time.split(':').map(s => parseInt(s, 10))
    if (isNaN(hh) || isNaN(mm)) return false
    if (mm !== 0 && mm !== 30) return false
    const dt = new Date(`${m.date}T${m.time}:00`)
    return !isNaN(dt.getTime()) && dt.getTime() > Date.now()
  })
  const formValid = grantValid && meetingsValid && (grantHoursNum > 0 || meetings.length > 0)

  const updateMeeting = (idx: number, patch: Partial<MeetingRow>) => {
    setMeetings(prev => prev.map((m, i) => i === idx ? {...m, ...patch} : m))
  }

  const removeMeeting = (idx: number) => {
    setMeetings(prev => prev.filter((_, i) => i !== idx))
  }

  const handleSubmit = async () => {
    if (!organizationId) return
    setSubmitting(true)
    setError(null)
    setPartialSuccess(null)

    const failures: string[] = []

    if (grantHoursNum > 0) {
      try {
        await CreditGrantController.createCreditGrant(organizationId, {
          type: grantType,
          hours: grantHoursNum,
          validUntil: grantValidForever ? null : (grantValidUntil || null),
          comment: grantComment || null,
        })
      } catch (e: any) {
        failures.push(`Grant: ${e?.message ?? 'error'}`)
      }
    }

    if (meetings.length > 0) {
      const totalMeetingHours = meetings.reduce((s, m) => s + m.durationMinutes / 60, 0)
      try {
        await StatusCallAccountController.grantStatusCallHours(organizationId, {
          hours: totalMeetingHours,
          comment: grantComment || 'Auto-grant for scheduled meetings',
        })
      } catch (e: any) {
        failures.push(`Status call grant: ${e?.message ?? 'error'}`)
        setSubmitting(false)
        setError(failures.join('\n'))
        return
      }
    }

    let scheduledCount = 0
    for (let i = 0; i < meetings.length; i++) {
      const m = meetings[i]
      try {
        await StatusCallController.createStatusCall({
          organizationId: organizationId,
          scheduledAt: `${m.date}T${m.time}:00`,
          durationMinutes: m.durationMinutes,
          title: m.title || null,
          notes: null,
          meetingLink: null,
          participantEmails: [],
        })
        scheduledCount++
      } catch (e: any) {
        failures.push(`Spotkanie #${i + 1} (${m.date} ${m.time}): ${e?.response?.status === 409 ? 'slot zajęty / brak puli' : (e?.message ?? 'error')}`)
      }
    }

    setSubmitting(false)
    if (failures.length === 0) {
      setGrantHours('0')
      setGrantComment('')
      setMeetings([])
      if (organizationId) loadSummaries(organizationId)
    } else {
      const okPart = scheduledCount > 0 ? `Zaplanowano ${scheduledCount} z ${meetings.length} spotkań.\n` : ''
      setError(okPart + failures.join('\n'))
      if (scheduledCount > 0) {
        setPartialSuccess(`Część operacji się powiodła — pozostałe błędy poniżej.`)
      }
    }
  }

  return <Drawer
    anchor="right"
    open={isOpen}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: calendarVisible ? '85%' : '50%',
        marginTop: '64px',
        height: 'calc(100% - 64px)',
        position: 'fixed',
        right: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s ease',
      }
    }}
  >
    <Box sx={{px: 3, pt: 3}}>
      <Typography variant="h6">{tr('title')}</Typography>
      {organizationName && <Typography variant="body2" color="text.secondary">{organizationName}</Typography>}
      <Divider sx={{my: 2}}/>
    </Box>

    <Box ref={scrollContainerRef} sx={{flex: 1, overflowY: 'auto', px: 3}}>
    <Paper variant="outlined" sx={{p: 2, mb: 2, bgcolor: 'grey.50'}}>
      <Typography variant="subtitle2" gutterBottom>{tr('currentState.title')}</Typography>
      <Box display="flex" gap={3} flexWrap="wrap">
        <Box>
          <Typography variant="caption" color="text.secondary">{tr('currentState.creditBalance')}</Typography>
          <Typography variant="h6">{creditSummary ? `${creditSummary.balanceHours}h` : '—'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{tr('currentState.statusCallAvailable')}</Typography>
          <Typography variant="h6">
            {statusCallSummary ? `${statusCallSummary.hoursAvailable}h / ${statusCallSummary.hoursTotal}h` : '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{tr('currentState.upcomingCalls')}</Typography>
          <Typography variant="h6">{statusCallSummary?.upcomingCalls?.length ?? 0}</Typography>
        </Box>
      </Box>
      {statusCallSummary?.upcomingCalls && statusCallSummary.upcomingCalls.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary">{tr('currentState.upcomingList')}</Typography>
          <Box component="ul" sx={{m: 0, pl: 2}}>
            {statusCallSummary.upcomingCalls.slice(0, 5).map(c => (
              <li key={c.id}>
                <Typography variant="body2">
                  {new Date(c.scheduledAt).toLocaleString('pl-PL', {dateStyle: 'short', timeStyle: 'short'})} ({c.durationMinutes}min){c.title ? ` — ${c.title}` : ''}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Paper>

    <Typography variant="subtitle1">{tr('section.supportHours')}</Typography>
    <Box display="flex" flexDirection="column" gap={2} mt={1}>
      <TextField
        select
        size="small"
        label={tr('grantType')}
        value={grantType}
        onChange={(e) => setGrantType(e.target.value as CreditGrantTypeEnum)}
      >
        {Object.values(CreditGrantTypeEnum).map(type => (
          <MenuItem key={type} value={type}>{t(`credit-support.grant-type.${type}`)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        type="number"
        label={tr('grantHours')}
        value={grantHours}
        onChange={(e) => setGrantHours(e.target.value)}
        inputProps={{step: 0.5, min: 0}}
      />
      <Box display="flex" gap={2} alignItems="center">
        <TextField
          size="small"
          type="date"
          label={tr('grantValidUntil')}
          InputLabelProps={{shrink: true}}
          inputProps={{min: todayIso}}
          value={grantValidUntil}
          onChange={(e) => setGrantValidUntil(e.target.value)}
          disabled={grantValidForever}
          sx={{flex: 1}}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={grantValidForever}
              onChange={(e) => setGrantValidForever(e.target.checked)}
            />
          }
          label={tr('grantValidForever')}
        />
      </Box>
      <TextField
        size="small"
        label={tr('grantComment')}
        value={grantComment}
        onChange={(e) => setGrantComment(e.target.value)}
        multiline
        rows={2}
      />
    </Box>

    <Divider sx={{my: 2}}/>

    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="subtitle1">{tr('section.meetings')}</Typography>
      <Box ml="auto" display="flex" gap={1}>
        <Button
          size="small"
          startIcon={<EventIcon/>}
          onClick={() => setCalendarVisible(v => !v)}
          variant={calendarVisible ? 'contained' : 'outlined'}
        >
          {tr(calendarVisible ? 'hideCalendar' : 'showCalendar')}
        </Button>
        <Button
          size="small"
          startIcon={<AddIcon/>}
          onClick={() => setMeetings(prev => [...prev, buildEmptyMeeting()])}
        >
          {tr('addMeeting')}
        </Button>
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary">{tr('meetingsHint')}</Typography>

    <Collapse in={calendarVisible}>
      <Paper elevation={1} sx={{p: 1, mt: 1, borderRadius: 2}}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          locale={plLocale}
          initialView="dayGridMonth"
          headerToolbar={{left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek'}}
          height={600}
          firstDay={1}
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          slotDuration="00:30:00"
          allDaySlot={false}
          weekends={false}
          selectable={true}
          validRange={{start: todayIso}}
          events={calendarEvents}
          datesSet={(info) => fetchCalendar(
            info.start.toISOString().slice(0, 10),
            info.end.toISOString().slice(0, 10)
          )}
          dateClick={(info) => {
            const d = info.date
            const pad = (n: number) => String(n).padStart(2, '0')
            const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
            const today = new Date(); today.setHours(0, 0, 0, 0)
            const clickedDay = new Date(d); clickedDay.setHours(0, 0, 0, 0)
            if (clickedDay.getTime() < today.getTime()) return
            const hasTimeOfDay = info.view.type !== 'dayGridMonth'
            const minutes = hasTimeOfDay ? (d.getMinutes() < 30 ? 0 : 30) : 0
            const hours = hasTimeOfDay ? d.getHours() : 10
            const time = `${pad(hours)}:${pad(minutes)}`
            setMeetings(prev => [...prev, {date, time, durationMinutes: 60, title: ''}])
          }}
        />
      </Paper>
    </Collapse>

    <Box display="flex" flexDirection="column" gap={2} mt={1}>
      {meetings.map((m, idx) => (
        <Box key={idx} display="flex" gap={1} alignItems="flex-start">
          <TextField
            size="small"
            type="date"
            label={tr('meetingDate')}
            InputLabelProps={{shrink: true}}
            inputProps={{min: todayIso}}
            value={m.date}
            onChange={(e) => updateMeeting(idx, {date: e.target.value})}
            sx={{flex: 1}}
          />
          <TextField
            size="small"
            type="time"
            label={tr('meetingTime')}
            InputLabelProps={{shrink: true}}
            inputProps={{step: 1800}}
            value={m.time}
            onChange={(e) => updateMeeting(idx, {time: e.target.value})}
            sx={{width: 110}}
          />
          <TextField
            select
            size="small"
            label={tr('meetingDuration')}
            value={m.durationMinutes}
            onChange={(e) => updateMeeting(idx, {durationMinutes: parseInt(e.target.value as string, 10)})}
            sx={{width: 100}}
          >
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={60}>60</MenuItem>
          </TextField>
          <TextField
            size="small"
            label={tr('meetingTitle')}
            value={m.title}
            onChange={(e) => updateMeeting(idx, {title: e.target.value})}
            sx={{flex: 1}}
          />
          <IconButton onClick={() => removeMeeting(idx)} aria-label="remove">
            <DeleteIcon/>
          </IconButton>
        </Box>
      ))}
      {meetings.length === 0 &&
        <Typography variant="caption" color="text.secondary">{tr('noMeetings')}</Typography>}
    </Box>

    {error && <Alert severity={partialSuccess ? 'warning' : 'error'} sx={{mt: 2, whiteSpace: 'pre-wrap'}}>{error}</Alert>}
    </Box>

    <Box sx={{px: 3, py: 2, borderTop: 1, borderColor: 'divider'}} display="flex" gap={2}>
      <Button variant="outlined" onClick={onClose} sx={{flex: 1}} disabled={submitting}>
        {t('common.cancel.title')}
      </Button>
      <Button variant="contained" onClick={handleSubmit} disabled={!formValid || submitting}>
        {tr('submit')}
      </Button>
    </Box>
  </Drawer>
}
